import { createContext, useCallback, useContext, useState } from "react";
import { toast } from "../components/Toast";
import {
  pushDisplayTemplate,
  updateDisplayTemplate,
} from "../../settings/api/displayTemplates";

const PreviewModeContext = createContext();

export const PreviewModeProvider = ({ children }) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [, setOriginalTemplate] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const enterPreviewMode = useCallback((template) => {
    const copy = JSON.parse(JSON.stringify(template));
    setOriginalTemplate(JSON.parse(JSON.stringify(template)));
    setPreviewTemplate(copy);
    setIsPreviewMode(true);
    setIsDirty(false);
  }, []);

  const exitPreviewMode = useCallback(() => {
    setIsPreviewMode(false);
    setPreviewTemplate(null);
    setOriginalTemplate(null);
    setIsDirty(false);
  }, []);

  const updateCardConfig = useCallback((configKey, cardKey, changes) => {
    setPreviewTemplate((prev) => {
      if (!prev) return prev;
      const config = prev[configKey] || [];
      const updated = config.map((c) =>
        c.key === cardKey ? { ...c, ...changes } : c,
      );
      return { ...prev, [configKey]: updated };
    });
    setIsDirty(true);
  }, []);

  const updateColorway = useCallback((slot, color) => {
    setPreviewTemplate((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        colorway_config: { ...prev.colorway_config, [slot]: color },
      };
    });
    setIsDirty(true);
  }, []);

  const saveAndPush = useCallback(async () => {
    if (!previewTemplate?.id) return;
    setSaving(true);
    try {
      await updateDisplayTemplate(previewTemplate.id, {
        dashboard_config: previewTemplate.dashboard_config,
        analytics_config: previewTemplate.analytics_config,
        asset_analytics_config: previewTemplate.asset_analytics_config,
        colorway_config: previewTemplate.colorway_config,
      });
      await pushDisplayTemplate();
      toast.success("Template saved and pushed to all members");
      exitPreviewMode();
      window.location.reload();
    } catch {
      toast.error("Failed to save and push template");
    } finally {
      setSaving(false);
    }
  }, [previewTemplate, exitPreviewMode]);

  const saveDraft = useCallback(async () => {
    if (!previewTemplate?.id) return;
    setSaving(true);
    try {
      await updateDisplayTemplate(previewTemplate.id, {
        dashboard_config: previewTemplate.dashboard_config,
        analytics_config: previewTemplate.analytics_config,
        asset_analytics_config: previewTemplate.asset_analytics_config,
        colorway_config: previewTemplate.colorway_config,
      });
      toast.success("Template draft saved");
      setOriginalTemplate(JSON.parse(JSON.stringify(previewTemplate)));
      setIsDirty(false);
    } catch {
      toast.error("Failed to save template draft");
    } finally {
      setSaving(false);
    }
  }, [previewTemplate]);

  const discard = useCallback(() => {
    exitPreviewMode();
  }, [exitPreviewMode]);

  return (
    <PreviewModeContext.Provider
      value={{
        isPreviewMode,
        previewTemplate,
        isDirty,
        saving,
        enterPreviewMode,
        exitPreviewMode,
        updateCardConfig,
        updateColorway,
        saveAndPush,
        saveDraft,
        discard,
      }}
    >
      {children}
    </PreviewModeContext.Provider>
  );
};

export const usePreviewMode = () => useContext(PreviewModeContext);
